import React, { createContext, useEffect, useMemo, useState } from 'react';

import { Partner } from 'src/modules/designStudio/helpers/Partner';

enum Mode {
  DEFAULT = 'default',
  GUEST = 'guest',
  SHARE = 'share',
  PREVIEW = 'preview',
  INTERACTIVE_RENDERING = 'interactiveRendering',
}

const defaultContext = {
  mode: Mode.DEFAULT,
  partner: Partner.DEFAULT,
  hasPartnerIdBeenFetched: false,
};

/**
 * Org 141718 is the JH homeowner org. We want to identify the partner as JH for this org.
 */
const OrgPartnerMapping: Record<number, Partner> = {
  141718: Partner.JAMES_HARDIE,
  557089: Partner.MUELLER,
};

const PartnerIdMapping: { [key: number]: Partner } = {
  9: Partner.JAMES_HARDIE,
};

const mapPartnerIdToEnum = (
  id: number,
  fallback: Partner = Partner.DEFAULT,
) => {
  if (PartnerIdMapping[id]) {
    return PartnerIdMapping[id];
  }
  return fallback;
};

const context = createContext<{
  mode: Mode;
  partner: Partner;
  hasPartnerIdBeenFetched: boolean;
}>(defaultContext);

const validatePartner = (query?: string): Partner => {
  const lowerCaseQuery = query?.toLowerCase();
  const isValidPartner = Object.values(Partner).includes(
    lowerCaseQuery as Partner,
  );
  if (isValidPartner) {
    return lowerCaseQuery as Partner;
  }
  return Partner.DEFAULT;
};

const determineInitialPartner = (
  mode: Mode,
  partnerQuery?: string,
  partnerId?: number,
) => {
  if (mode === Mode.INTERACTIVE_RENDERING) {
    return Partner.JAMES_HARDIE;
  }

  if (partnerQuery) {
    return validatePartner(partnerQuery);
  }

  return mapPartnerIdToEnum(partnerId ?? -1);
};

const Provider = ({
  mode,
  partnerQuery,
  partnerId,
  children,
  orgId,
}: {
  mode: Mode;
  partnerQuery?: string;
  partnerId?: number;
  children: React.ReactNode;
  orgId?: number;
}) => {
  const [hasPartnerIdBeenFetched, setHasPartnerIdBeenFetched] = useState(false);

  const [partner, setPartner] = useState(
    determineInitialPartner(mode, partnerQuery, partnerId),
  );

  useEffect(() => {
    const handleSetPartner = () => {
      if (mode === Mode.INTERACTIVE_RENDERING) {
        setPartner(Partner.JAMES_HARDIE);
      } else if (mode !== Mode.GUEST && partnerId) {
        setPartner(mapPartnerIdToEnum(partnerId));
        if (orgId && OrgPartnerMapping[orgId] !== undefined) {
          setPartner(OrgPartnerMapping[orgId]);
        }
      }
      setHasPartnerIdBeenFetched(true);
    };
    void handleSetPartner();
  }, [mode, setPartner, partnerId, orgId]);

  const value = useMemo(
    () => ({ mode, partner, hasPartnerIdBeenFetched }),
    [mode, partner, hasPartnerIdBeenFetched],
  );

  return <context.Provider value={value}>{children}</context.Provider>;
};

export { Provider, context, Mode, mapPartnerIdToEnum };
